import React, { useEffect, useRef } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import './CGU.css';

// Composant pour les particules - Réutilisé et adapté depuis Hero.js
const CGUParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    // Référence au canvas
    const canvas = canvasRef.current;
    if (!canvas) return; // Sortir si le canvas n'existe pas
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // Sortir si le contexte n'existe pas
    
    // Pour suivre si le composant est monté
    let isMounted = true;
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      if (!canvas || !isMounted) return;
      
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    // Appliquer le redimensionnement initial
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 8, 150);
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      if (!canvas || !isMounted) return;
      
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    // Classe pour les particules
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        if (!isMounted) return;
        
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran seulement si le canvas existe toujours
        if (canvas && canvas.width && canvas.height) {
          const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
          
          if (this.x < margin) {
            this.x = margin;
            this.speedX *= -0.8;
          } else if (this.x > canvas.width - margin) {
            this.x = canvas.width - margin;
            this.speedX *= -0.8;
          }
          
          if (this.y < margin) {
            this.y = margin;
            this.speedY *= -0.8;
          } else if (this.y > canvas.height - margin) {
            this.y = canvas.height - margin;
            this.speedY *= -0.8;
          }
        }
      }
      
      draw() {
        if (!ctx || !isMounted) return;
        
        // Centre plus défini avec moins de halo
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta avec variation
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 290 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      if (!isMounted || !canvas) return;
      
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    
    // Initialiser les particules
    init();
    
    // Animer les particules
    let animationFrameId = null;
    
    const animate = () => {
      if (!canvas || !ctx || !isMounted) {
        return;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particlesArray.length; i++) {
        if (particlesArray[i] && isMounted) {
          particlesArray[i].update();
          particlesArray[i].draw();
        }
      }
      
      if (isMounted) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    // Lancer l'animation
    animate();
    
    // Nettoyage
    return () => {
      isMounted = false;
      
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="cgu-particles" />
  );
};

function CGU() {
  const { t } = useLanguage();
  
  // Assurer que la page s'affiche tout en haut au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Mettre à jour le titre de la page
    document.title = `${t('cguTitle')} - Quizzer`;
    
    // Mettre à jour la meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('cguIntro'));
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = t('cguIntro');
      document.head.appendChild(meta);
    }
  }, [t]);
  
  return (
    <div className="cgu-page">
      <Header />
      <main className="main-content">
        <section className="cgu-section">
          <CGUParticles />
          <div className="container">
            <div className="cgu-container fade-in">
              <div className="cgu-header">
                <h1 className="cgu-title">{t('cguTitle')}</h1>
                <p className="cgu-intro">{t('cguIntro')}</p>
              </div>
              
              <div className="cgu-content">
                <div className="cgu-section-item">
                  <h2>{t('cguSection1Title') || "1. Acceptation des Conditions"}</h2>
                  <p>{t('cguSection1Content') || "En accédant à l'application Quizzer, vous acceptez d'être lié par ces conditions d'utilisation, toutes les lois et réglementations applicables, et vous acceptez que vous êtes responsable du respect des lois locales applicables. Si vous n'acceptez pas l'une de ces conditions, vous êtes interdit d'utiliser ou d'accéder à cette application."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection2Title') || "2. Licence d'utilisation"}</h2>
                  <p>{t('cguSection2Content') || "L'autorisation est accordée de télécharger temporairement une copie de l'application Quizzer pour un usage personnel, non commercial transitoire uniquement. Il s'agit de l'octroi d'une licence, et non d'un transfert de titre, et sous cette licence, vous ne pouvez pas :"}</p>
                  <ul>
                    <li>{t('cguSection2Item1') || "Modifier ou copier le contenu de l'application;"}</li>
                    <li>{t('cguSection2Item2') || "Utiliser le contenu à des fins commerciales;"}</li>
                    <li>{t('cguSection2Item3') || "Tenter de décompiler ou de désosser tout logiciel contenu dans l'application;"}</li>
                    <li>{t('cguSection2Item4') || "Supprimer tout droit d'auteur ou autres notations de propriété des contenus;"}</li>
                    <li>{t('cguSection2Item5') || "Transférer le contenu à une autre personne ou \"dupliquer\" le contenu sur un autre serveur."}</li>
                  </ul>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection3Title') || "3. Exactitude des contenus"}</h2>
                  <p>{t('cguSection3Content') || "Les contenus de l'application Quizzer sont fournis à titre informatif et divertissant uniquement. Bien que nous nous efforcions de maintenir l'exactitude des informations, nous ne garantissons pas que le contenu soit exact, complet, fiable, à jour ou sans erreurs."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection4Title') || "4. Droits de propriété intellectuelle"}</h2>
                  <p>{t('cguSection4Content') || "Tous les contenus présents ou rendus disponibles à travers l'application Quizzer - texte, graphiques, logos, images, ainsi que leur compilation - sont la propriété de Quizzer ou de ses fournisseurs de contenu et sont protégés par les lois sur la propriété intellectuelle."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection5Title') || "5. Utilisation des données"}</h2>
                  <p>{t('cguSection5Content') || "Quizzer collecte et utilise vos données conformément à notre Politique de Confidentialité. En utilisant notre application, vous consentez à cette collecte et utilisation de vos données."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection6Title') || "6. Comptes utilisateurs"}</h2>
                  <p>{t('cguSection6Content') || "Lorsque vous créez un compte sur Quizzer, vous êtes responsable de maintenir la confidentialité de votre compte et mot de passe et de restreindre l'accès à votre ordinateur. Vous acceptez d'assumer la responsabilité de toutes les activités qui se produisent sous votre compte ou mot de passe."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection7Title') || "7. Modifications des conditions"}</h2>
                  <p>{t('cguSection7Content') || "Quizzer peut réviser ces conditions d'utilisation à tout moment sans préavis. En utilisant cette application, vous acceptez d'être lié par la version alors actuelle de ces termes et conditions."}</p>
                </div>
                
                <div className="cgu-section-item">
                  <h2>{t('cguSection8Title') || "8. Loi applicable"}</h2>
                  <p>{t('cguSection8Content') || "Ces conditions sont régies et interprétées conformément aux lois françaises, et vous vous soumettez irrévocablement à la juridiction exclusive des tribunaux de cette juridiction."}</p>
                </div>
                
                <div className="cgu-section-item last">
                  <h2>{t('cguSection9Title') || "9. Contact"}</h2>
                  <p>{t('cguSection9Content') || "Pour toute question concernant ces conditions d'utilisation, veuillez nous contacter via notre page de contact."}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default CGU; 